import { base64ToBlob } from '@/utils/base64ToBlob';
import { getAxiosClient } from '../../client';
import { makeApiRequest } from '../../makeApiRequest';
import type { ApiResponse } from '../../responses';
import type { ISuppliersService } from './ISuppliersService';
import type {
    SupplierCreateRequest,
    SupplierListQuery,
    SupplierPaymentCreateRequest,
    SupplierPaymentsQuery,
    SupplierStatementQuery,
    SupplierWriteRequest,
} from './requests';
import type {
    GetSupplierDetailsResponse,
    GetSupplierListResponse,
    GetSupplierPaymentsResponse,
    GetSupplierPickerResponse,
    GetSupplierStatementResponse,
    SupplierPaymentWriteResponse,
    SupplierWriteResponse,
} from './responses';
import { SuppliersEndpoints } from './suppliersEndpoints';
import {
    toSupplierDetailsResponse,
    toSupplierPaymentItem,
    toSupplierPaymentsResponse,
    toSupplierStatementResponse,
} from './suppliersMapping';

type SuppliersServiceOptions = {
    baseUrl: string;
};

export class SuppliersService implements ISuppliersService {
    private readonly baseUrl: string;

    constructor({ baseUrl }: SuppliersServiceOptions) {
        this.baseUrl = baseUrl;
    }

    public getSupplierList(): Promise<ApiResponse<GetSupplierListResponse>> {
        const url = SuppliersEndpoints.list();
        return makeApiRequest<GetSupplierListResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).get(url),
        );
    }

    public async getSupplierDetails(
        query: SupplierListQuery,
    ): Promise<ApiResponse<GetSupplierDetailsResponse>> {
        const url = SuppliersEndpoints.details();
        // The bos-api binds these case-insensitively; `search` is omitted when
        // blank so an empty box returns the unfiltered page.
        const params: Record<string, string | number | boolean> = {
            page: query.page,
            pageSize: query.pageSize,
        };
        const search = query.search?.trim();
        if (search) {
            params.search = search;
        }
        if (query.includeArchived) {
            params.includeArchived = true;
        }
        const result = await makeApiRequest<GetSupplierDetailsResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).get(url, { params }),
        );
        return result.success
            ? {
                  success: true,
                  response: toSupplierDetailsResponse(result.response),
              }
            : result;
    }

    public getSupplierPicker(): Promise<
        ApiResponse<GetSupplierPickerResponse>
    > {
        // BOS-83: every non-deleted supplier, unpaginated — for dropdowns and
        // import name-matching that need the whole set at once.
        const url = SuppliersEndpoints.picker();
        return makeApiRequest<GetSupplierPickerResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).get(url),
        );
    }

    public createSupplier(
        body: SupplierCreateRequest,
    ): Promise<ApiResponse<SupplierWriteResponse>> {
        const url = SuppliersEndpoints.create();

        // bos-api binds [FromForm] on POST /suppliers — name (required),
        // optional serialNumber/phone/email, optional `file`. Packing as
        // multipart lets the server validate the image and persist the
        // supplier in one round-trip.
        const formData = new FormData();
        formData.append('name', body.name);
        if (body.serialNumber !== null) {
            formData.append('serialNumber', body.serialNumber);
        }
        if (body.phone !== null) {
            formData.append('phone', body.phone);
        }
        if (body.email !== null) {
            formData.append('email', body.email);
        }
        if (body.imageUpload) {
            formData.append(
                'file',
                base64ToBlob(
                    body.imageUpload.dataBase64,
                    body.imageUpload.mimeType,
                ),
                body.imageUpload.fileName,
            );
        }

        return makeApiRequest<SupplierWriteResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).post(url, formData),
        );
    }

    public updateSupplier(
        id: string,
        body: SupplierWriteRequest,
    ): Promise<ApiResponse<SupplierWriteResponse>> {
        const url = SuppliersEndpoints.update(id);
        return makeApiRequest<SupplierWriteResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).put(url, body),
        );
    }

    public deleteSupplier(id: string): Promise<ApiResponse<void>> {
        const url = SuppliersEndpoints.delete(id);
        return makeApiRequest<void>(
            getAxiosClient({ baseURL: this.baseUrl }).delete(url),
        );
    }

    public setSupplierArchived(
        id: string,
        archived: boolean,
    ): Promise<ApiResponse<SupplierWriteResponse>> {
        // Two routes rather than a PATCH with a flag: archiving and
        // unarchiving are separate acts server-side, and only one of them can
        // be refused.
        const url = archived
            ? SuppliersEndpoints.archive(id)
            : SuppliersEndpoints.unarchive(id);
        return makeApiRequest<SupplierWriteResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).post(url),
        );
    }

    public async getSupplierPayments(
        id: string,
        query: SupplierPaymentsQuery,
    ): Promise<ApiResponse<GetSupplierPaymentsResponse>> {
        const url = SuppliersEndpoints.payments(id);
        const result = await makeApiRequest<GetSupplierPaymentsResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).get(url, {
                params: { page: query.page, pageSize: query.pageSize },
            }),
        );
        return result.success
            ? {
                  success: true,
                  response: toSupplierPaymentsResponse(result.response),
              }
            : result;
    }

    public async createSupplierPayment(
        id: string,
        body: SupplierPaymentCreateRequest,
    ): Promise<ApiResponse<SupplierPaymentWriteResponse>> {
        const url = SuppliersEndpoints.payments(id);
        const result = await makeApiRequest<SupplierPaymentWriteResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).post(url, body),
        );
        return result.success
            ? {
                  success: true,
                  response: toSupplierPaymentItem(result.response),
              }
            : result;
    }

    public deleteSupplierPayment(
        id: string,
        paymentId: string,
    ): Promise<ApiResponse<void>> {
        const url = SuppliersEndpoints.deletePayment(id, paymentId);
        return makeApiRequest<void>(
            getAxiosClient({ baseURL: this.baseUrl }).delete(url),
        );
    }

    public async getSupplierStatement(
        id: string,
        query: SupplierStatementQuery,
    ): Promise<ApiResponse<GetSupplierStatementResponse>> {
        const url = SuppliersEndpoints.statement(id);
        // Omitted rather than sent empty: a blank `from` would bind to the
        // default DateOnly on the server and window the statement to year 1.
        const params: Record<string, string> = {};
        if (query.from) {
            params.from = query.from;
        }
        if (query.to) {
            params.to = query.to;
        }
        const result = await makeApiRequest<GetSupplierStatementResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).get(url, { params }),
        );
        return result.success
            ? {
                  success: true,
                  response: toSupplierStatementResponse(result.response),
              }
            : result;
    }
}
